import { useMemo, ReactNode } from 'react';

import { AnimatePresence, motion, MotionProps } from 'framer-motion';

export type AnimationType = 'horizontal' | 'vertical';

interface SlideTransitionProps extends MotionProps {
  children: ReactNode;
  animationType?: AnimationType;
  direction: 'first' | 'second';
  exitBeforeEnter?: boolean;
}

const transition = {
  ease: 'easeInOut',
  duration: 0.4,
};

const bottomToUpVariants = {
  initial: {
    opacity: 0,
    y: 100,
    transition,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition,
  },
  exit: {
    opacity: 0,
    y: -100,
    transition,
  },
};

const upToBottomVariants = {
  initial: {
    opacity: 0,
    y: 100,
    transition,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition,
  },
  exit: {
    opacity: 0,
    y: -100,
    transition,
  },
};

const leftToRightVariants = {
  initial: {
    opacity: 0,
    x: 100,
    transition,
  },
  enter: {
    opacity: 1,
    x: 0,
    transition,
  },
  exit: {
    opacity: 0,
    x: -100,
    transition,
  },
};

const rightToLeftVariants = {
  initial: {
    opacity: 0,
    x: -100,
    transition,
  },
  enter: {
    opacity: 1,
    x: 0,
    transition,
  },
  exit: {
    opacity: 0,
    x: 100,
    transition,
  },
};

export function SlideTransition({
  children,
  direction,
  animationType = 'horizontal',
  exitBeforeEnter = true,
  ...rest
}: SlideTransitionProps): JSX.Element {
  const memoVariants = useMemo(() => {
    const animation = {
      horizontal: {
        first: leftToRightVariants,
        second: rightToLeftVariants,
      },
      vertical: {
        first: bottomToUpVariants,
        second: upToBottomVariants,
      },
    };

    return animation[animationType][direction];
  }, [direction, animationType]);

  return (
    <AnimatePresence exitBeforeEnter={exitBeforeEnter}>
      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={memoVariants}
        transition={transition}
        style={{
          overflowY: animationType === 'vertical' ? 'hidden' : undefined,
          overflowX: animationType === 'horizontal' ? 'hidden' : undefined,
        }}
        {...rest}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
